// The transcript spine (PRD 002 F1) as a single chat-card feed (ChatGPT/Claude-
// style). Human speaker runs render as avatar+bubble; the AI participant runs
// FULLY AUTO — there is no confirm step and no raised hand. Every contribution it
// sends interweaves inline at its contextual anchor (AiTurn.afterSegmentId): a
// `present` bubble, or a typed card (candidate/insight/caution/note) with its own
// colour + icon. A contribution still being worked on renders as a loading card
// until a follow-up signal fills it. A bottom scrubber (SignalTimeline) places
// every contribution on a timeline you can drag to scroll the feed.

import {
  ArrowDown,
  Bug,
  Check,
  CheckCheck,
  Clock,
  Eye,
  EyeOff,
  Loader2,
  Megaphone,
  Sparkles,
  Volume2,
  VolumeX,
  type LucideIcon
} from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { ContactAvatar } from '@/components/shell/ContactAvatar'
import { SignalTimeline } from '@/components/shell/SignalTimeline'
import { KIND_STYLE, TONE, type Tone } from '@/components/shell/aiTurnStyle'
import { isCuesMuted, setCuesMuted } from '@/lib/ai/audioCues'
import { isFocusModalEnabled, setFocusModalEnabled } from '@/lib/ai/focusModal'
import { isAutoSpeakEnabled, setAutoSpeakEnabled } from '@/lib/ai/speech'
import { voicePlayer } from '@/lib/ai/voicePlayer'
import { resolveIdentity, type Contact } from '@/lib/contacts/types'
import { cn } from '@/lib/utils'
import type { AiStatus, AiTurn } from '@/lib/ai/types'
import type { SegmentDelivery } from '@/lib/session/sessionStore'
import type { Speaker, SpeakerId, TranscriptSegment } from '@/lib/transcription/types'

const SPEAKER_FALLBACK = {
  providerLabel: 'Speaker',
  displayName: null,
  color: 'text-muted-foreground'
} as const

const PIN_THRESHOLD_PX = 80

const AI_STATUS_LABEL: Record<AiStatus, string> = {
  idle: 'Listening',
  thinking: 'Thinking…',
  raised: 'Hand raised',
  presenting: 'Working…',
  error: 'Paused'
}

function Avatar({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'mt-0.5 grid size-7 shrink-0 place-items-center rounded-full text-code font-semibold',
        className
      )}
    >
      {children}
    </span>
  )
}

// A WhatsApp-style delivery receipt for a transcript line forwarded to the AI:
// one grey tick once the gateway acked it (`sent`), two accent ticks once the
// agent actually drained it (`heard`). Lets the room eyeball the round-trip
// latency. Absent until the line has been forwarded at all.
function DeliveryTicks({ delivery }: { delivery?: SegmentDelivery }) {
  if (!delivery) return null
  const heard = delivery === 'heard'
  return (
    <span
      title={heard ? 'Claude heard this' : 'Sent to Claude'}
      className={cn(
        'mt-0.5 inline-flex items-center',
        heard ? 'text-accent' : 'text-muted-foreground/60'
      )}
    >
      {heard ? <CheckCheck className="size-3" /> : <Check className="size-3" />}
    </span>
  )
}

function SpeakerCard({
  segment,
  speaker,
  contact,
  interim,
  tail,
  delivery
}: {
  segment: TranscriptSegment
  speaker: Speaker | undefined
  contact?: Contact
  interim?: boolean
  // Live partial for the SAME speaker, shown inline (greyed) at the end of this
  // bubble rather than bumped to a new line below (feedback.md, Tuesday Demo).
  tail?: string
  // Bridge delivery receipt for this line (absent = not forwarded / no AI).
  delivery?: SegmentDelivery
}) {
  const identity = resolveIdentity(speaker ?? SPEAKER_FALLBACK, contact)
  return (
    <div className="flex gap-2.5 rounded-xl transition-shadow" data-segid={segment.id}>
      <ContactAvatar
        name={identity.name}
        color={identity.color}
        avatar={identity.avatar}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <div className={cn('mb-0.5 text-label font-semibold', identity.color)}>
          {identity.name}
        </div>
        <div className="flex items-end gap-1.5">
          <div
            className={cn(
              'inline-block max-w-full rounded-2xl rounded-tl-sm border border-border bg-surface px-3 py-2 text-body leading-relaxed text-text',
              interim && 'border-dashed italic text-muted-foreground'
            )}
          >
            {segment.text}
            {tail && (
              <span className="text-muted-foreground italic">
                {segment.text && ' '}
                {tail}
                <span className="text-muted-foreground"> ▍</span>
              </span>
            )}
          </div>
          {!interim && <DeliveryTicks delivery={delivery} />}
        </div>
      </div>
    </div>
  )
}

// A small tick that marks an AI contribution as handled. Hidden while the
// contribution is still streaming (nothing to check off yet).
function DoneToggle({
  completed,
  onToggle
}: {
  completed?: boolean
  onToggle?: () => void
}) {
  if (!onToggle) return null
  return (
    <button
      type="button"
      onClick={onToggle}
      title={completed ? 'Mark as not done' : 'Mark as done'}
      aria-pressed={completed}
      className={cn(
        'grid size-7 shrink-0 place-items-center rounded-md border transition-colors',
        completed
          ? 'border-success bg-success text-bg'
          : 'border-border text-muted-foreground hover:border-success/60 hover:text-success'
      )}
    >
      {/* Always shown (even unticked) so it's a discoverable control. */}
      <Check className="size-4" strokeWidth={3} />
    </button>
  )
}

// A small icon button that voices a contribution aloud on demand (ElevenLabs).
// Hidden when no voice is configured (onSpeak absent) or while still loading.
function SpeakButton({ onSpeak }: { onSpeak?: () => void }) {
  if (!onSpeak) return null
  return (
    <button
      type="button"
      onClick={onSpeak}
      title="Speak this aloud"
      className="grid size-7 shrink-0 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:border-accent/60 hover:text-accent"
    >
      <Volume2 className="size-4" />
    </button>
  )
}

function AiBubble({
  turn,
  onToggleDone,
  onSpeak
}: {
  turn: AiTurn
  onToggleDone?: (id: string) => void
  onSpeak?: (id: string) => void
}) {
  const loading = turn.streaming && !turn.text
  return (
    <div className={cn('flex gap-2.5', turn.completed && 'opacity-45 saturate-50')}>
      <Avatar className="bg-accent/15 text-accent">
        <Sparkles className="size-3.5" />
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5 text-label font-semibold text-accent">
          <span>AI participant</span>
          {!turn.streaming && (
            <span className="ml-auto flex items-center gap-1">
              <SpeakButton onSpeak={onSpeak && (() => onSpeak(turn.id))} />
              <DoneToggle
                completed={turn.completed}
                onToggle={onToggleDone && (() => onToggleDone(turn.id))}
              />
            </span>
          )}
        </div>
        <div className="inline-block max-w-full rounded-2xl rounded-tl-sm border border-accent/40 bg-accent/5 px-3 py-2 text-body leading-relaxed whitespace-pre-wrap text-text">
          {loading ? (
            <span className="flex items-center gap-2 text-muted-foreground italic">
              {turn.queued ? (
                <>
                  <Clock className="size-3.5 shrink-0" />
                  Queued · {turn.label || 'Working…'}
                </>
              ) : (
                <>
                  <Loader2 className="size-3.5 shrink-0 animate-spin" />
                  {turn.label || 'Working…'}
                </>
              )}
            </span>
          ) : (
            <>
              {turn.text}
              {turn.streaming && <span className="text-muted-foreground"> ▍</span>}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// The shared typed "signal" card (report style): icon + label, body text, a
// one-clause "why", and a loading state while the contribution is pending.
function SignalCard({
  tone,
  Icon,
  label,
  text,
  detail,
  loading,
  loadingLabel,
  queued,
  completed,
  onToggle,
  onSpeak
}: {
  tone: Tone
  Icon: LucideIcon
  label: string
  text: string
  detail?: string
  loading?: boolean
  loadingLabel?: string
  queued?: boolean
  completed?: boolean
  onToggle?: () => void
  onSpeak?: () => void
}) {
  const t = TONE[tone]
  return (
    <div
      className={cn(
        'rounded-lg border border-l-2 px-2.5 py-2',
        t.border,
        t.bg,
        completed && 'opacity-45 saturate-50'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-1.5 text-label font-semibold tracking-wide uppercase',
          t.text
        )}
      >
        <Icon className="size-3.5 shrink-0" />
        <span className="truncate">{label}</span>
        {!loading && (
          <span className="ml-auto flex items-center gap-1 normal-case">
            <SpeakButton onSpeak={onSpeak} />
            <DoneToggle completed={completed} onToggle={onToggle} />
          </span>
        )}
      </div>
      {loading ? (
        <p className="mt-1 flex items-center gap-2 text-body-sm text-muted-foreground italic">
          {queued ? (
            <>
              <Clock className="size-3.5 shrink-0" />
              Queued · {loadingLabel || 'Working…'}
            </>
          ) : (
            <>
              <Loader2 className="size-3.5 shrink-0 animate-spin" />
              {loadingLabel || 'Working…'}
            </>
          )}
        </p>
      ) : (
        <>
          <p className="mt-1 text-body-sm text-text">{text}</p>
          {detail && <p className="mt-0.5 text-label text-muted-foreground">↳ {detail}</p>}
        </>
      )}
    </div>
  )
}

// A done card collapses to a single muted line — a tick, the kind label, and the
// text truncated — so finished contributions stay scannable in the feed without
// taking a full card. Clicking "uncheck" expands it again.
function CollapsedTurn({
  turn,
  onToggleDone
}: {
  turn: AiTurn
  onToggleDone?: (id: string) => void
}) {
  const label = turn.kind === 'present' ? 'AI participant' : KIND_STYLE[turn.kind].label
  return (
    <div className="flex items-center gap-2 py-0.5 text-label text-muted-foreground">
      <Check className="size-3.5 shrink-0 text-success" strokeWidth={3} />
      <span className="min-w-0 flex-1 truncate">
        <span className="font-semibold tracking-wide uppercase">{label}</span>
        <span className="text-muted-foreground/70">: {turn.text}</span>
      </span>
      {onToggleDone && (
        <button
          type="button"
          onClick={() => onToggleDone(turn.id)}
          title="Mark as not done"
          className="shrink-0 rounded px-1 text-muted-foreground/70 underline-offset-2 hover:text-text hover:underline"
        >
          uncheck
        </button>
      )}
    </div>
  )
}

function renderTurn(
  t: AiTurn,
  onToggleDone?: (id: string) => void,
  onSpeak?: (id: string) => void
) {
  // A finished contribution collapses to one line (any kind).
  if (t.completed) {
    return <CollapsedTurn key={t.id} turn={t} onToggleDone={onToggleDone} />
  }
  if (t.kind === 'present') {
    return (
      <AiBubble key={t.id} turn={t} onToggleDone={onToggleDone} onSpeak={onSpeak} />
    )
  }
  const style = KIND_STYLE[t.kind]
  const loading = t.streaming && !t.text
  return (
    <SignalCard
      key={t.id}
      tone={style.tone}
      Icon={style.Icon}
      label={style.label}
      text={t.text}
      detail={t.detail}
      loading={loading}
      loadingLabel={t.label}
      queued={t.queued}
      completed={t.completed}
      onToggle={!loading && onToggleDone ? () => onToggleDone(t.id) : undefined}
      onSpeak={!loading && onSpeak ? () => onSpeak(t.id) : undefined}
    />
  )
}

export function TranscriptView({
  finals,
  interim,
  speakers,
  aiTurns,
  aiStatus,
  aiDetail,
  aiPaired,
  contactsById,
  delivery,
  onToggleDone,
  onSpeak,
  debugEnabled,
  onToggleDebug
}: {
  finals: TranscriptSegment[]
  interim: TranscriptSegment[]
  speakers: Record<SpeakerId, Speaker>
  aiTurns: AiTurn[]
  aiStatus: AiStatus
  aiDetail: string
  aiPaired: boolean
  contactsById: Record<string, Contact>
  delivery: Record<string, SegmentDelivery>
  onToggleDone?: (id: string) => void
  /** Voice a contribution aloud on demand. Absent = no ElevenLabs key, so the
   *  speak buttons and the auto-speak toggle are hidden. */
  onSpeak?: (id: string) => void
  /** Debug Panel visibility + toggle (only wired when a bridge is paired). */
  debugEnabled?: boolean
  onToggleDebug?: () => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [pinned, setPinned] = useState(true)
  const [muted, setMuted] = useState(isCuesMuted)
  const [autoSpeak, setAutoSpeak] = useState(isAutoSpeakEnabled)
  const [focusModal, setFocusModal] = useState(isFocusModalEnabled)

  // Follow the live edge while pinned; new content arrives via finals/interim/AI.
  useEffect(() => {
    if (!pinned) return
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [finals, interim, aiTurns, pinned])

  function onScroll() {
    const el = scrollRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    setPinned(distance <= PIN_THRESHOLD_PX)
  }

  function scrollToLive() {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
    setPinned(true)
  }

  // Scroll the feed to the transcript line a contribution refers to (null = the
  // live edge — the signal had no resolvable anchor), and flash it.
  function scrollToSegment(segmentId: string | null) {
    const root = scrollRef.current
    if (!root) return
    if (!segmentId) {
      scrollToLive()
      return
    }
    const sel = window.CSS?.escape ? window.CSS.escape(segmentId) : segmentId
    const el = root.querySelector<HTMLElement>(`[data-segid="${sel}"]`)
    if (!el) {
      scrollToLive()
      return
    }
    setPinned(false)
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    el.classList.add('ring-2', 'ring-accent')
    window.setTimeout(() => el.classList.remove('ring-2', 'ring-accent'), 1200)
  }

  const empty = finals.length === 0 && interim.length === 0 && aiTurns.length === 0

  // Bucket inline AI turns by the segment they follow (null = before any).
  const turnsAfter = new Map<string, AiTurn[]>()
  const headTurns: AiTurn[] = []
  for (const t of aiTurns) {
    if (t.afterSegmentId === null) {
      headTurns.push(t)
      continue
    }
    const arr = turnsAfter.get(t.afterSegmentId)
    if (arr) arr.push(t)
    else turnsAfter.set(t.afterSegmentId, [t])
  }
  const known = new Set(finals.map((s) => s.id))
  const orphanTurns = aiTurns.filter(
    (t) => t.afterSegmentId !== null && !known.has(t.afterSegmentId)
  )

  // Type-ahead inline: a live partial that continues the current speaker's run
  // folds into the last final's bubble (greyed tail) instead of bumping to a new
  // line; a partial from a *different* speaker still gets its own bubble.
  const lastFinal = finals[finals.length - 1]
  let inlineTail = ''
  const standaloneInterim: TranscriptSegment[] = []
  for (const seg of interim) {
    if (!seg.text) continue
    if (seg.speakerId === lastFinal?.speakerId) {
      inlineTail = inlineTail ? `${inlineTail} ${seg.text}` : seg.text
    } else {
      standaloneInterim.push(seg)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="h-full space-y-4 overflow-y-auto px-1 py-4"
        >
          {empty ? (
            <p className="text-body-sm text-muted-foreground">
              Waiting for speech… start talking and the transcript appears here.
            </p>
          ) : (
            <>
              {headTurns.map((t) => renderTurn(t, onToggleDone, onSpeak))}
              {finals.map((seg, i) => (
                <Fragment key={seg.id}>
                  <SpeakerCard
                    segment={seg}
                    speaker={speakers[seg.speakerId]}
                    contact={contactsById[speakers[seg.speakerId]?.contactId ?? '']}
                    tail={i === finals.length - 1 ? inlineTail || undefined : undefined}
                    delivery={delivery[seg.id]}
                  />
                  {turnsAfter
                    .get(seg.id)
                    ?.map((t) => renderTurn(t, onToggleDone, onSpeak))}
                </Fragment>
              ))}
              {standaloneInterim.map((seg) => (
                <SpeakerCard
                  key={seg.id}
                  segment={seg}
                  speaker={speakers[seg.speakerId]}
                  contact={contactsById[speakers[seg.speakerId]?.contactId ?? '']}
                  interim
                />
              ))}
              {orphanTurns.map((t) => renderTurn(t, onToggleDone, onSpeak))}
            </>
          )}
        </div>

        {!pinned && (
          <div className="absolute inset-x-0 bottom-3 flex justify-center">
            <Button size="sm" variant="outline" onClick={scrollToLive}>
              <ArrowDown /> Scroll to live
            </Button>
          </div>
        )}
      </div>

      <div className="mt-1 shrink-0 border-t border-border pt-3">
        {!aiPaired ? (
          <p className="text-label text-muted-foreground">
            Pair your Claude Code bridge in{' '}
            <a href="#/settings" className="text-accent hover:underline">
              settings
            </a>{' '}
            to let an AI participant listen, surface ideas, and contribute inline.
          </p>
        ) : (
          <>
            <div className="mb-2 flex items-center gap-1.5 text-label text-muted-foreground">
              <Sparkles
                className={cn('size-3.5', aiStatus === 'error' ? 'text-danger' : 'text-accent')}
              />
              <span>{AI_STATUS_LABEL[aiStatus]}</span>
              {aiDetail && <span className="truncate">· {aiDetail}</span>}
              <span className="ml-auto flex items-center gap-2">
                {/* Primary surfacing (PRD 008 Part B): the focus modal. */}
                <button
                  type="button"
                  onClick={() => {
                    const next = !focusModal
                    setFocusModalEnabled(next)
                    setFocusModal(next)
                  }}
                  title={
                    focusModal
                      ? 'Focus modal on — a salient signal pops as an unmissable modal'
                      : 'Focus modal off — signals only appear inline in the feed'
                  }
                  aria-pressed={focusModal}
                  className={cn(
                    'flex items-center gap-1 rounded px-1 py-0.5',
                    focusModal ? 'text-accent' : 'hover:text-text'
                  )}
                >
                  {focusModal ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                  <span>Focus</span>
                </button>
                {/* Debug Panel toggle (live-test instrumentation). Only wired
                    when a bridge is paired. */}
                {onToggleDebug && (
                  <button
                    type="button"
                    onClick={onToggleDebug}
                    title={
                      debugEnabled
                        ? 'Debug panel on — reflex events, stats + live param tuning'
                        : 'Debug panel off'
                    }
                    aria-pressed={debugEnabled}
                    className={cn(
                      'flex items-center gap-1 rounded px-1 py-0.5',
                      debugEnabled ? 'text-accent' : 'hover:text-text'
                    )}
                  >
                    <Bug className="size-3.5" />
                    <span>Debug</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const next = !muted
                    setCuesMuted(next)
                    setMuted(next)
                  }}
                  title={muted ? 'Unmute AI sounds' : 'Mute AI sounds'}
                  aria-pressed={muted}
                  className="flex items-center gap-1 rounded px-1 py-0.5 hover:text-text"
                >
                  {muted ? <VolumeX className="size-3.5" /> : <Volume2 className="size-3.5" />}
                  <span>{muted ? 'Muted' : 'Sound'}</span>
                </button>
                {/* Voice is kept but demoted (PRD 008 §4.2): secondary, muted,
                    last — the modal is the default surfacing now. */}
                {onSpeak && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = !autoSpeak
                      setAutoSpeakEnabled(next)
                      setAutoSpeak(next)
                      // Turning Voice off stops anything currently being read.
                      if (!next) voicePlayer.stop()
                    }}
                    title={
                      autoSpeak
                        ? 'Voice on — the AI reads a card aloud as it pops in the focus modal'
                        : 'Voice off — the AI stays text-only unless you press a card’s speak button'
                    }
                    aria-pressed={autoSpeak}
                    className={cn(
                      'flex items-center gap-1 rounded px-1 py-0.5 text-muted-foreground/70',
                      autoSpeak ? 'text-accent' : 'hover:text-text'
                    )}
                  >
                    <Megaphone className="size-3" />
                    <span>Voice</span>
                  </button>
                )}
              </span>
            </div>
            <SignalTimeline
              turns={aiTurns}
              scrollRef={scrollRef}
              onJump={scrollToSegment}
              onToggleDone={onToggleDone}
            />
          </>
        )}
      </div>
    </div>
  )
}
